import React from 'react';

import { createRoot } from 'react-dom/client';

import { browserTracingIntegration, init, replayIntegration } from '@sentry/browser';
import { registerSW } from 'virtual:pwa-register';

import * as unRegisterListenerUser from 'appRedux/sagas/user/profile';
import * as unRegisterListenerAuth from 'appRedux/sagas/user/token';

import { firebaseApp } from './firebase/firebase';
import NextApp from './NextApp';
import {
  handleServiceWorkerNeedRefresh,
  maybeRepromptServiceWorkerUpdate,
} from './util/handleServiceWorkerNeedRefresh';
import { migrateLegacyServiceWorkerIfNeeded } from './util/migrateLegacyServiceWorker';
import { recoverIfDeployedVersionMismatch } from './util/recoverIfDeployedVersionMismatch';
import { isBenignClientNoise } from './util/sentry/isBenignClientNoise';
import { isBenignServiceWorkerError } from './util/sentry/isBenignServiceWorkerError';
import { reloadOnceOnChunkLoadFailure } from './util/sentry/reloadOnceOnChunkLoadFailure';
import { scrubInviteSecretDeep } from './util/sentry/scrubInviteSecret';

const OFFLINE_PLUGIN_UPDATE_INTERVAL_MS = 1000 * 60 * 60 * 4; // same as former OfflinePlugin autoUpdate
const SW_UPDATE_REPROMPT_DEBOUNCE_MS = 500;

const container = document.getElementById('root');
const root = createRoot(container);

const render = Component => {
  root.render(<Component />);
};

const initializeSentry = environment => {
  init({
    environment,
    dsn: 'https://accb4b320f9247ed92ff755fa4f44fc3@o436503.ingest.sentry.io/5755449',
    release: `${SYSTEM.version}`,
    replaysSessionSampleRate: 1.0,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      browserTracingIntegration(),
      replayIntegration({ beforeAddRecordingEvent: scrubInviteSecretDeep }),
    ],
    tracesSampleRate: 1.0,
    // The invite link secret signs a pending invitee in; keep it out of every payload.
    beforeSend: scrubInviteSecretDeep,
    beforeSendTransaction: scrubInviteSecretDeep,
    beforeBreadcrumb: scrubInviteSecretDeep,
    ignoreErrors: [
      /^Rejected$/,
      /^Error: Rejected$/,
      /InvalidStateError.*ServiceWorker/i,
      /Failed to (update|register) a ServiceWorker/i,
      /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/i,
      /Unexpected token '<'/,
      /Loading chunk [\d]+ failed/i,
      /ChunkLoadError/i,
      /Unable to preload CSS for /i,
      /Failed to fetch dynamically imported module/i,
      /error loading dynamically imported module/i,
    ],
  });
};

const suppressBenignClientRejections = () => {
  window.addEventListener('unhandledrejection', event => {
    if (
      isBenignServiceWorkerError(event.reason) ||
      isBenignClientNoise(event.reason)
    ) {
      event.preventDefault();
    }
    reloadOnceOnChunkLoadFailure(event.reason);
  });

  window.addEventListener('error', event => {
    const payload = event.error || event.message;
    if (isBenignServiceWorkerError(payload) || isBenignClientNoise(payload)) {
      event.preventDefault();
    }
    reloadOnceOnChunkLoadFailure(payload);
  });
};

const initializeServiceWorker = loadTime => {
  // vite-plugin-pwa owns the SW (filename: service-worker.js). Do not also call
  // CRA registerServiceWorker — dual register caused WORKLION-PWA-B7/BD/J7.
  // Update UX:
  // - Waiting worker already present on this load (refresh without accepting
  //   the modal, or CRA→Vite waiting SW) → force-apply + guaranteed reload.
  // - Update within 7s of boot → auto-apply (former OfflinePlugin behavior).
  // - Mid-session update → modal; dismiss keeps a pending flag so the prompt
  //   returns on the next visibility change while still outdated.
  // - Poll for updates every 4 hours while the tab is alive.
  let registrationRef = null;
  let repromptDebounceId;

  const updateSW = registerSW({
    immediate: true,
    onRegisteredSW(_swUrl, registration) {
      if (!registration) {
        return;
      }

      registrationRef = registration;

      // Critical escape hatch for the reported production bug: a waiting worker
      // after reload (or CRA→Vite waiting SW) means SKIP_WAITING + document
      // reload never completed. Activate without the modal via the shared
      // need-refresh decision path (forceApply).
      if (registration.waiting) {
        handleServiceWorkerNeedRefresh({
          updateSW,
          loadTime,
          registration,
          forceApply: true,
        });
      }

      const checkForUpdate = () => {
        registration.update();
      };

      const updateIntervalId = setInterval(checkForUpdate, OFFLINE_PLUGIN_UPDATE_INTERVAL_MS);

      const onVisibilityChange = () => {
        if (document.visibilityState !== 'visible') {
          return;
        }
        checkForUpdate();
        window.clearTimeout(repromptDebounceId);
        repromptDebounceId = window.setTimeout(() => {
          maybeRepromptServiceWorkerUpdate({
            hasWaitingWorker: Boolean(registrationRef?.waiting),
            updateSW,
            registration: registrationRef,
          });
        }, SW_UPDATE_REPROMPT_DEBOUNCE_MS);
      };
      document.addEventListener('visibilitychange', onVisibilityChange);

      window.addEventListener(
        'pagehide',
        () => {
          clearInterval(updateIntervalId);
          window.clearTimeout(repromptDebounceId);
          document.removeEventListener('visibilitychange', onVisibilityChange);
        },
        { once: true },
      );
    },
    onNeedRefresh() {
      // Mid-session installs keep the modal (after the boot window).
      // Boot-time waiting is also force-applied in onRegisteredSW; both paths
      // share applyServiceWorkerUpdate's in-flight lock.
      handleServiceWorkerNeedRefresh({
        updateSW,
        loadTime,
        registration: registrationRef,
      });
    },
    onRegisterError() {
      // Benign SW registration noise is filtered in Sentry ignoreErrors.
    },
  });
};

const handleHotModuleReplacement = () => {
  if (import.meta.hot) {
    import.meta.hot.accept('./NextApp', () => {
      unRegisterListenerUser.listenerUnregister()();
      unRegisterListenerAuth.listenerUnregister()();
      render(NextApp);
    });
  }
};

const initializeApp = async () => {
  const environment = firebaseApp?.options?.projectId;

  if (!import.meta.hot) {
    const reloadingForSwMigration = await migrateLegacyServiceWorkerIfNeeded();
    if (reloadingForSwMigration) {
      return;
    }

    // Network version.json vs baked SYSTEM.version — escapes stuck SW after deploys
    // when the update modal / SW lifecycle did not activate the new build.
    const reloadingForVersionMismatch = await recoverIfDeployedVersionMismatch();
    if (reloadingForVersionMismatch) {
      return;
    }
  }

  render(NextApp);

  if (import.meta.hot) {
    handleHotModuleReplacement();
  } else {
    suppressBenignClientRejections();
    initializeSentry(environment);
    const loadTime = Date.now();
    initializeServiceWorker(loadTime);
  }
};

initializeApp();
